import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/Button';
import { HubSpotForm } from './HubSpotForm';
interface TimedPopupProps {
  delaySeconds?: number;
}
export function TimedPopup({
  delaySeconds = 10
}: TimedPopupProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [showForm, setShowForm] = useState(false);
  useEffect(() => {
    // Check if user has already dismissed the popup
    const hasSeenPopup = localStorage.getItem('hmv_popup_dismissed');
    if (hasSeenPopup) {
      return;
    }
    // Set timer to show popup after delay
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delaySeconds * 1000);
    return () => clearTimeout(timer);
  }, [delaySeconds]);
  const handleClose = () => {
    setIsVisible(false);
    localStorage.setItem('hmv_popup_dismissed', 'true');
  };
  const handleJoinClick = () => {
    setShowForm(true);
  };
  if (!isVisible) {
    return null;
  }
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-fadeIn" onClick={handleClose} data-id="element-430" />

      {/* Popup */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-431">
        <div className="bg-[#fbf7dd] rounded-2xl shadow-2xl max-w-md w-full relative pointer-events-auto animate-slideUp flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()} data-id="element-432">
          {/* Close Button - Fixed at top right, always visible */}
          <button onClick={handleClose} className="absolute top-4 right-4 z-10 text-gray-400 hover:text-gray-600 transition-colors bg-[#fbf7dd] rounded-full p-1 shadow-sm" aria-label="Close popup" data-id="element-433">
            <X className="h-6 w-6" data-id="element-434" />
          </button>

          {/* Scrollable Content Area */}
          <div className="overflow-y-auto p-8 pr-12" data-id="element-435">
            {!showForm ? <>
                {/* Badge */}
                <div className="inline-block bg-[#FFC107] text-black px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4" data-id="element-436">
                  Limited Spaces Available
                </div>

                {/* Heading */}
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 leading-tight" data-id="element-437">
                  Don't miss out on{' '}
                  <span className="text-[#175445]" data-id="element-438">early bird pricing</span>
                </h3>

                {/* Description */}
                <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-439">
                  Join the priority list today and secure your storage bay at
                  our introductory rates.
                </p>

                {/* Benefits */}
                <ul className="space-y-3 mb-8" data-id="element-440">
                  <li className="flex items-start" data-id="element-441">
                    <svg className="h-5 w-5 text-[#175445] mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" data-id="element-442">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-443" />
                    </svg>
                    <span className="text-gray-700 text-sm" data-id="element-444">
                      Priority access to available bays
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-445">
                    <svg className="h-5 w-5 text-[#175445] mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" data-id="element-446">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-447" />
                    </svg>
                    <span className="text-gray-700 text-sm" data-id="element-448">
                      Lock in early bird pricing
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-449">
                    <svg className="h-5 w-5 text-[#175445] mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" data-id="element-450">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-451" />
                    </svg>
                    <span className="text-gray-700 text-sm" data-id="element-452">
                      No commitment required
                    </span>
                  </li>
                </ul>

                {/* CTA Button */}
                <Button variant="secondary" size="lg" className="w-full text-lg" onClick={handleJoinClick} data-id="element-453">
                  Join Priority List
                </Button>

                <p className="text-xs text-gray-400 text-center mt-4" data-id="element-454">
                  Registering is free and carries no obligation
                </p>
              </> : <>
                <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-455">
                  Join the Priority List
                </h3>
                <p className="text-gray-600 mb-6 text-sm" data-id="element-456">
                  Early registrants receive priority access and introductory
                  pricing.
                </p>
                <HubSpotForm data-id="element-457" />
              </>}
          </div>
        </div>
      </div>

      <style data-id="element-458">{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }

        .animate-slideUp {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
    </>;
}